import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Search, Building2, Plus, Upload, Download, FileSpreadsheet, Archive, FileText, CheckSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import EmployeeForm from "@/pages/EmployeeForm";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfirm } from "@/components/ConfirmDialog";
import { Textarea } from "@/components/ui/textarea";

function initials(name = "") {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

function downloadTemplate() {
  const token = localStorage.getItem("access_token");
  fetch(`${API}/employees/csv-template`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
    .then(r => r.blob())
    .then(b => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(b);
      link.download = "employees-template.csv";
      document.body.appendChild(link); link.click(); link.remove();
    });
}

export default function Employees() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [file, setFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkDept, setBulkDept] = useState("");
  const [assignDocOpen, setAssignDocOpen] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const [e, d] = await Promise.all([api.get("/employees"), api.get("/departments")]);
    setRows(e.data); setDepartments(d.data);
  };
  useEffect(() => { load(); }, []);

  const depts = useMemo(() => ["all", ...Array.from(new Set(rows.map(r => r.department).filter(d => d && d.trim())))], [rows]);

  const filtered = useMemo(() => rows.filter(r =>
    (dept === "all" || r.department === dept) &&
    (!q || r.name.toLowerCase().includes(q.toLowerCase()) ||
      r.email.toLowerCase().includes(q.toLowerCase()) ||
      r.designation.toLowerCase().includes(q.toLowerCase()))
  ), [rows, dept, q]);

  const bulkDeptOptions = useMemo(() => departments.filter(d => d?.name?.trim()), [departments]);

  const runBulk = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a CSV"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/employees/bulk-import?dry_run=${dryRun}`, fd);
      setBulkResult(data);
      if (!dryRun && data.created > 0) load();
      toast.success(dryRun ? `Preview: ${data.created} would import, ${data.skipped} skip, ${data.errors} errors` : `${data.created} imported · ${data.skipped} skipped · ${data.errors} errors`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkArchive = async () => {
    const ids = Array.from(selected);
    const ok = await confirm({
      tone: "warning",
      title: `Archive ${ids.length} employees?`,
      description: "Archived employees are hidden from active lists but their history is retained. Their direct reports (if any) will be reparented automatically. You can restore any of them from their profile page.",
      confirmLabel: `Archive ${ids.length}`,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/employees/bulk-archive", { employee_ids: ids });
      toast.success(`Archived ${data.archived}${data.reparented ? ` · ${data.reparented} report(s) reparented` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkChangeDept = async (newDept) => {
    if (!newDept) return;
    const ids = Array.from(selected);
    const ok = await confirm({
      tone: "info",
      title: `Move ${ids.length} employees to "${newDept}"?`,
      description: "Their department will change. Reporting lines and other details stay untouched.",
      confirmLabel: `Move to ${newDept}`,
    });
    if (!ok) { setBulkDept(""); return; }
    try {
      const { data } = await api.post("/employees/bulk-department", { employee_ids: ids, department: newDept });
      toast.success(`Moved ${data.updated} to ${newDept}`);
      setSelected(new Set()); setBulkDept("");
      load();
    } catch (e) { toast.error(formatApiError(e)); setBulkDept(""); }
  };

  return (
    <div className="space-y-6" data-testid="employees-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">People</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Employee directory</h1>
          <p className="mt-1 text-sm text-slate-500">All {rows.length} people at Techtreno, in one place.</p>
        </div>
        {isReviewer && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setBulkOpen(true); setBulkResult(null); setFile(null); }} data-testid="bulk-import-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Bulk import
            </Button>
            <Button onClick={() => setAddOpen(true)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-employee-btn">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Add employee
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, role, email…" className="pl-9 h-11 bg-white" data-testid="employees-search" />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-56 h-11 bg-white" data-testid="dept-filter">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            {depts.map(d => <SelectItem key={d} value={d}>{d === "all" ? "All departments" : d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isReviewer && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-red-50/60 border border-red-200 rounded-xl px-4 py-2.5" data-testid="emp-bulk-bar">
          <CheckSquare className="h-4 w-4 text-[#DC2626]" />
          <span className="text-sm font-medium text-[#DC2626]">{selected.size} selected</span>
          <span className="text-slate-300 mx-1">·</span>
          <Button size="sm" variant="outline" onClick={bulkArchive} data-testid="emp-bulk-archive">
            <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
          </Button>
          <Select value={bulkDept} onValueChange={async (v) => { setBulkDept(v); await bulkChangeDept(v); }}>
            <SelectTrigger className="w-52 h-9" data-testid="emp-bulk-dept-select">
              <SelectValue placeholder="Change department…" />
            </SelectTrigger>
            <SelectContent>
              {bulkDeptOptions.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setAssignDocOpen(true)} data-testid="emp-bulk-assign-doc">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Assign document
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto" data-testid="emp-bulk-clear">Clear</Button>
        </div>
      )}

      <Card className="rounded-xl border-slate-200/60 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
          {isReviewer && (
            <div className="col-span-1 flex items-center">
              <Checkbox
                checked={filtered.length > 0 && filtered.every(e => selected.has(e.id))}
                onCheckedChange={(v) => setSelected(v ? new Set(filtered.map(e => e.id)) : new Set())}
                data-testid="emp-select-all"
              />
            </div>
          )}
          <div className={isReviewer ? "col-span-4" : "col-span-5"}>Employee</div>
          <div className="col-span-3">Department</div>
          <div className="col-span-2">Branch</div>
          <div className="col-span-2">Status</div>
        </div>
        {filtered.map(e => (
          <div key={e.id} className={`grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 hover:bg-slate-50/70 transition-all items-center ${selected.has(e.id) ? "bg-red-50/30" : ""}`} data-testid={`emp-row-${e.employee_code}`}>
            {isReviewer && (
              <div className="col-span-1 flex items-center">
                <Checkbox
                  checked={selected.has(e.id)}
                  onCheckedChange={() => toggleSelect(e.id)}
                  data-testid={`emp-select-${e.employee_code}`}
                />
              </div>
            )}
            <Link to={`/employees/${e.id}`} className={`${isReviewer ? "col-span-4" : "col-span-5"} flex items-center gap-3 hover:no-underline`}>
              <Avatar className="h-10 w-10">
                {e.avatar_url && <AvatarImage src={`${API}${e.avatar_url}`} alt={e.name} />}
                <AvatarFallback className="bg-red-100 text-[#DC2626] text-xs font-semibold">{initials(e.name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{e.name}</div>
                <div className="text-xs text-slate-500 truncate">{e.designation} · {e.employee_code}</div>
              </div>
            </Link>
            <div className="col-span-3 text-sm text-slate-700 flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.5} />
              {e.department}
            </div>
            <div className="col-span-2 text-sm text-slate-600">{e.branch}</div>
            <div className="col-span-2">
              <Badge className={
                e.status === "Active" ? "bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50"
                : e.status === "Notice Period" ? "bg-orange-50 text-orange-700 border-0 hover:bg-orange-50"
                : e.status === "Exited" ? "bg-red-50 text-red-700 border-0 hover:bg-red-50"
                : "bg-slate-100 text-slate-700 border-0 hover:bg-slate-100"
              }>{e.status}</Badge>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No employees match your filters.</div>}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
          <EmployeeForm mode="add" departments={departments} employees={rows} onDone={() => { setAddOpen(false); load(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk import employees</DialogTitle></DialogHeader>
          <form onSubmit={runBulk} className="space-y-4">
            <div className="rounded-lg border border-red-100 bg-red-50/40 p-3 text-xs text-slate-600 flex items-center justify-between gap-3">
              <div>
                Upload a CSV with columns: <span className="font-mono">employee_code, name, email, designation, department, joining_date</span> (+ optional phone, branch, reporting_manager, blood_group, pan, aadhaar, skills).
              </div>
              <Button type="button" variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template">
                <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} />Template
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label>CSV file</Label>
              <Input type="file" accept=".csv" onChange={e => { setFile(e.target.files?.[0]); setBulkResult(null); }} data-testid="bulk-file" required />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="dry-run-check" />
              Preview only (dry-run) — nothing will be created
            </label>
            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="bulk-submit">
                <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.8} />{dryRun ? "Preview" : "Import"}
              </Button>
            </div>
            {bulkResult && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2 text-sm max-h-72 overflow-y-auto">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[10px] uppercase text-slate-500">Created</div><div className="font-display text-2xl font-bold text-emerald-600">{bulkResult.created}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Skipped</div><div className="font-display text-2xl font-bold text-orange-600">{bulkResult.skipped}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-500">Errors</div><div className="font-display text-2xl font-bold text-red-600">{bulkResult.errors}</div></div>
                </div>
                {bulkResult.skipped_rows?.length > 0 && (
                  <div className="text-xs">
                    <div className="font-semibold text-slate-700 mt-2">Skipped:</div>
                    {bulkResult.skipped_rows.slice(0,8).map((r) => <div key={`skip-${r.row}`} className="text-slate-600">Row {r.row}: {r.reason}</div>)}
                  </div>
                )}
                {bulkResult.error_rows?.length > 0 && (
                  <div className="text-xs">
                    <div className="font-semibold text-red-700 mt-2">Errors:</div>
                    {bulkResult.error_rows.slice(0,8).map((r) => <div key={`err-${r.row}`} className="text-red-600">Row {r.row}: {r.reason}</div>)}
                  </div>
                )}
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <BulkAssignDocDialog
        open={assignDocOpen}
        onClose={() => setAssignDocOpen(false)}
        employeeIds={Array.from(selected)}
        employees={rows.filter(r => selected.has(r.id))}
        onDone={() => { setAssignDocOpen(false); setSelected(new Set()); }}
      />
    </div>
  );
}

function BulkAssignDocDialog({ open, onClose, employeeIds, employees, onDone }) {
  const [category, setCategory] = useState("Personal");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    let ok = 0, fail = 0;
    for (const empId of employeeIds) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("employee_id", empId);
      if (description) fd.append("description", description);
      try {
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    if (ok > 0) toast.success(`Document assigned to ${ok} employee${ok > 1 ? "s" : ""}${fail ? ` · ${fail} failed` : ""}`);
    else toast.error(`Assignment failed for all ${fail} employee(s)`);
    setFile(null); setDescription("");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-assign-doc-dialog">
        <DialogHeader>
          <DialogTitle>Assign a document to {employeeIds.length} employees</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-slate-50/60 p-3 max-h-24 overflow-y-auto">
            <span className="font-semibold text-slate-700">Recipients:</span> {employees.map(e => e.name).slice(0, 6).join(", ")}{employees.length > 6 ? `, +${employees.length - 6} more` : ""}
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="bulk-doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Offer Letter", "Appointment Letter", "Appraisal Letter", "ID Proof", "Address Proof", "Bank Detail", "Personal", "Other"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulk-doc-file" required />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Appraisal letter FY26" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !file} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="bulk-doc-submit">
              <FileText className="h-4 w-4 mr-1.5" /> {busy ? "Assigning…" : `Assign to ${employeeIds.length}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
